import replace from 'lodash/replace'
import startsWith from 'lodash/startsWith'
import sumBy from 'lodash/sumBy'
import { forwardRef, InputHTMLAttributes, useRef } from 'react'
import NumberInput from 'react-phone-number-input/input'
import { hasText } from 'utils/helpers'

import cn from '@/utils/tailwind/cn'

type InputProps = {
  hasError?: boolean
  grayOutWhenDisabled?: boolean
  isOfficeUse?: boolean
  id?: string
  name?: string
  inputId?: string | null
} & InputHTMLAttributes<HTMLInputElement>

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { grayOutWhenDisabled, inputId, id, name, hasError, isOfficeUse, ...props },
    ref
  ) => {
    return (
      <input
        className={cn(
          'form-input block w-full rounded-md border transition focus:!border-primary focus:shadow-[0_0_0_2px_rgba(5,130,255,0.08)]',
          'text-gray-700 duration-150 ease-in-out sm:text-sm sm:leading-5',
          hasError && 'border-red-500',
          { 'disabled:bg-gray-100': grayOutWhenDisabled || isOfficeUse }
        )}
        id={inputId || id || name}
        name={name}
        ref={ref}
        {...props}
      />
    )
  }
)

export type PhoneNumberInputProps = {
  hasError?: boolean
  grayOutWhenDisabled?: boolean
  isOfficeUse?: boolean
  inputId?: string
  maxLength?: number
  value?: string
  onChange?: (value: string) => void
} & InputHTMLAttributes<HTMLInputElement>

const buildPhoneNumberValue = (inputValue: string): string | undefined => {
  const value = inputValue || ''
  if (!hasText(value)) return undefined
  if (value.length === 10 && value[0] !== '1') return `+1${value}`
  if (value[0] === '+') return value

  return `+${value}`
}

const calculateMaxLength = (currentValue: string) => {
  const initialCount = startsWith(currentValue, '1 ') ? 0 : -1
  const notNumberCount = sumBy(currentValue, (char) =>
    char !== ' ' && Number.isInteger(+char) ? 0 : 1
  )
  return initialCount + 11 + notNumberCount
}

export const PhoneNumberInput = ({
  hasError,
  disabled,
  maxLength,
  inputId,
  name,
  onChange,
  value = '',
  ...props
}: PhoneNumberInputProps) => {
  const inputRef = useRef<null | HTMLInputElement>(null)

  const inputValue = buildPhoneNumberValue(value)

  const maxLengthValue =
    maxLength ?? calculateMaxLength(inputRef?.current?.value || '')

  const handleChange = (value?: string) => {
    if (disabled || !value) return

    const replaced = replace(String(value), /\D/g, '')

    if ((replaced?.length ?? 0) > 12) {
      const sliced = replaced?.slice?.(0, 11)
      return onChange?.(sliced)
    }
    return onChange?.(replaced)
  }

  return (
    <NumberInput
      country="US"
      disabled={disabled}
      id={inputId || name}
      inputComponent={Input}
      maxLengthValue={maxLengthValue}
      name={name}
      onChange={handleChange}
      ref={inputRef}
      trueValue={value}
      value={inputValue}
      {...props}
    />
  )
}
